<!-- ══════════════════════════════════════════════════════════════
     NAVBAR
     ══════════════════════════════════════════════════════════════ -->

<!-- PDF Loading overlay -->
@if (pdfLoading()) {
<div class="pdf-loading-overlay">
  <div class="pdf-loading-box">
    <div class="spinner-border text-primary mb-3 pdf-spinner"></div>
    <div class="fw-semibold text-15">Generating PDF…</div>
    <div class="text-muted small mt-1">Please wait</div>
  </div>
</div>
}

@if (loading()) {
<div class="quote-loading d-flex align-items-center text-secondary fw-medium">
  <!-- Bouncing Dots Animation (Size from reference) -->
  <div class="d-flex gap-2 me-3 bouncer-inline-xl">
    <div class="bouncer-dot-xl bg-primary"></div>
    <div class="bouncer-dot-xl bg-primary"></div>
    <div class="bouncer-dot-xl bg-primary"></div>
  </div>

  <!-- Loading Text -->
  <span class="fs-5">Loading quotation preview...</span>
</div>

} @else {
<div class="quote-preview-shell mt-3">

  <!-- ═══════════ HEADER (full width row, sits above sidebar + main) ═══════════ -->
  <div class="quote-header mt-3">
      <div>
        <div class="quote-title">
          <span>#{{ formatQuotationNo(tripInfo()?.QuotationNo) }}</span>
          <span class="text-muted">•</span>
          <span>{{ tripInfo()?.ContactName || 'Guest' }}</span>
          <span class="text-muted">•</span>
          <span>{{ tripInfo()?.DestinationName || 'Destination' }}</span>
          <span class="text-muted">•</span>
          <span class="small text-muted">{{ tripInfo()?.AgencyName || 'Direct Query' }}</span>
          @if (isConverted()) {
          <span class="converted-pill"><i class="bx bx-check"></i>Converted</span>
          } @else if (isOnHold()) {
          <span class="onhold-pill"><i class="bx bx-time-five"></i>On Hold</span>
          } @else {
          <span class="status-chip">Quote Given</span>
          }
        </div>
        <div class="quote-meta">
          <span><i class="bx bx-calendar"></i>{{ tripInfo()?.StartDate | date:'dd MMM, yyyy' }}</span>
          <span>{{ durationLabel() }}</span>
          <span class="d-flex align-items-center gap-1">
            <i class="bx bx-group"></i>{{ totalGuestCount() }} Guests
            <button class="icon-btn-xs" mat-icon-button (click)="editGuest()"
              matTooltip="Edit guest">
              <mat-icon class="icon-xs">edit</mat-icon>
            </button>
          </span>
          <span><i class="bx bx-user"></i>{{ tripInfo()?.SalesPersonName || 'Unassigned' }}</span>
        </div>
      </div>

      <div class="quote-header-actions">
        <div class="quote-total text-end">
          <div class="small text-muted">Package (INR)</div>
          <strong>{{ formatCurrency(packageQuotePrice(packageTypes()[0]?.QuotePackageTypeId || 0)) }}</strong>
          @if (isGstIncluded(packageTypes()[0]?.QuotePackageTypeId || 0)) {
          <div class="small text-muted">Inc. {{ quoteHeader()?.GstPercent || 0 }}% GST</div>
          } @else {
          <div class="small text-muted">Excl. GST</div>
          }
        </div>
        <button type="button" class="btn btn-outline-primary btn-sm" (click)="editDetail()">
          <i class="bx bx-edit-alt me-1"></i>Edit Quote
        </button>
        <button type="button" class="btn btn-light btn-sm border" (click)="openShare('whatsapp')">
          <i class="bx bx-share-alt me-1"></i>Share
        </button>
        <button type="button" class="btn btn-danger btn-sm" (click)="downloadPdf()" [disabled]="pdfLoading()">
          @if (pdfLoading()) {
          <span class="spinner-border spinner-border-sm me-1"></span>Generating…
          } @else {
          <i class="bx bx-file-pdf me-1"></i>Download PDF
          }
        </button>
        <button type="button" class="btn btn-light btn-sm border" (click)="backToQuotes()">
          <i class="bx bx-arrow-back me-1"></i>All Quotes
        </button>
      </div>
    </div>

  <!-- ═══════════ SIDEBAR ═══════════ -->
  <aside class="quote-sidebar">
    <div class="small text-muted px-3 mb-2">{{ packageTypes().length }} Options</div>
    @for (pkg of packageTypes(); track pkg.QuotePackageTypeId; let idx = $index) {
    <div class="quote-option" [class.active]="idx === 0">
      <div class="option-price">{{ formatCurrency(packageQuotePrice(pkg.QuotePackageTypeId)) }}</div>
      <div class="small text-muted">{{ tripInfo()?.StartDate | date:'dd MMM' }} · {{ durationLabel() }} · {{
        totalGuestCount() }}A</div>
    </div>
    }
  </aside>

  <!-- ═══════════ MAIN ═══════════ -->
  <main class="quote-main">

    <!-- Tabs -->
    <div class="quote-tabs">
      <span [class.active]="activeTab() === 'basic'" (click)="setActiveTab('basic')" role="button">Basic Details</span>
      <span [class.active]="activeTab() === 'quotes'" (click)="setActiveTab('quotes')" role="button">All Quotes <b>{{
          packageTypes().length }}</b></span>
      @if (isConverted()) {
      <span [class.active]="activeTab() === 'docs'" (click)="setActiveTab('docs')" role="button">Docs</span>
      }
      <span [class.active]="activeTab() === 'activities'" (click)="setActiveTab('activities')"
        role="button">Activities</span>
    </div>

    <!-- ═══════════ BASIC DETAILS TAB ═══════════ -->
    @if (activeTab() === 'basic') {
    <div class="basic-details-layout">
      <div class="basic-details-main">
        <div class="quote-card">
          <div class="section-subtitle">Latest Quote</div>

          <div class="section-subtitle">Package Quote Price</div>

          <div class="package-price-grid mb-3">
            @for (pkg of packageTypes(); track pkg.QuotePackageTypeId; let idx = $index) {
            <div class="package-price"
              [class.package-price-converted]="isConverted() && pkg.QuotePackageTypeId === convertedPackageTypeId()"
              [class.package-price-onhold]="isOnHold() && pkg.QuotePackageTypeId === convertedPackageTypeId()">
              @if (isOnHold() && pkg.QuotePackageTypeId === convertedPackageTypeId()) {
              <span class="onhold-chip">Used for Holding</span>
              }
              <div class="package-name">Option {{ idx + 1 }}: {{ pkg.PackageTypeName }}</div>
              <div>
                <span class="currency">INR</span>
                <strong>{{ formatCurrency(packageQuotePrice(pkg.QuotePackageTypeId)) }}</strong>
                @if (isGstIncluded(pkg.QuotePackageTypeId)) {
                <span class="small text-muted ms-1">(inc. {{ quoteHeader()?.GstPercent || 0 }}% GST)</span>
                } @else {
                <span class="small text-muted ms-1">(excl. GST)</span>
                }
                @if (isOverallPricing(pkg.QuotePackageTypeId)) {
                <span class="small text-muted ms-1">(Overall Package Price)</span>
                }
              </div>
              <div class="small text-muted mb-2">
                INR {{ formatCurrency(packageCostPrice(pkg.QuotePackageTypeId)) }} (cost price)
              </div>
              @for (line of guestCategoryTotals(pkg.QuotePackageTypeId); track line.label) {
              <div class="small">
                {{ formatCurrency(line.amount) }} / {{ line.label }} x {{ line.count }} {{ line.paxLabel }}
              </div>
              }
            </div>
            }
          </div>

          @if (quoteHeader()?.CreatedOn) {
          <div class="small text-muted mb-2">
            Created on {{ quoteHeader()?.CreatedOn | date:'dd MMM yyyy, h:mm a' }}
            @if (tripInfo()?.SalesPersonName) { by {{ tripInfo()?.SalesPersonName }} }
          </div>
          }

          <span class="status-chip">Latest Quote</span>

          @if (!isConverted()) {
          <div class="mt-3">
            <button type="button" class="btn btn-outline-primary btn-sm" (click)="convertOrHoldUsingQuote()">
              Convert/On-Hold using Quote
            </button>
          </div>
          }

          <div class="quote-table mt-3 quote-table-single-col">
            <div class="quote-row quote-row-checkbox">
              <i class="bx bx-calendar"></i>
              <div>
                {{ tripInfo()?.StartDate | date:'dd MMM, yyyy' }} for {{ (tripInfo()?.NoOfNights || 0) + 1 }} Days
                <span>{{ totalGuestCount() }} Adults</span>
              </div>
            </div>
          </div>

          <div class="section-title mt-4">Services</div>

          @for (pkg of packageTypes(); track pkg.QuotePackageTypeId; let pkgIdx = $index) {
          <span class="option-label">Option {{ pkgIdx + 1 }}: {{ pkg.PackageTypeName }}</span>
          <div class="service-section mb-4">
            <div class="service-icon"><i class="bx bx-bed"></i></div>
            <div class="service-content">
              <h5>Accommodation</h5>
              <div class="quote-table mb-2">
                <div class="quote-row quote-head">
                  <div>Night</div>
                  <div>Hotel</div>
                  <div>Meal</div>
                  <div>Rooms</div>
                  <div class="text-end">Price</div>
                </div>
                @for (group of hotelGroupsByPackage(pkg.QuotePackageTypeId); track (group.main.QuoteHotelId || $index) +
                '-' + group.nightNumber) {
                <div class="quote-row">
                  <div>
                    {{ group.nightNumber }}{{ ordinal(group.nightNumber) }}
                    <span>{{ group.stayDate | date:'dd MMM' }}</span>
                  </div>
                  <div class="hotel-cell">
                    <div class="hotel-line hotel-line-main">
                      <span class="hotel-name">{{ group.main.HotelName }}</span>
                      <span>{{ group.main.LocationName || '-' }}, {{ group.main.HotelCategoryName || '-' }}</span>
                    </div>
                  </div>
                  <div>{{ group.main.MealPlan || '-' }}</div>
                  <div>
                    {{ group.main.NoOfRooms || 1 }} {{ group.main.RoomTypeName || 'Room' }}
                    <span>{{ group.main.AWEB || 0 }} AwEB + {{ group.main.CNB || 0 }} CNB</span>
                  </div>
                  <div class="price-cell">
                    <i class="bx bx-check-circle text-success"></i>
                    <span>INR</span>
                    {{ formatCurrency(group.maxPrice) }}
                  </div>
                </div>
                } @empty {
                <div class="empty-row">No accommodation added for this package.</div>
                }
              </div>

              <div class="section-total">
                Total <span>INR</span> {{ formatCurrency(packageHotelTotal(pkg.QuotePackageTypeId) +
                packageSpecialInclusionTotal(pkg.QuotePackageTypeId)) }}
              </div>
            </div>
          </div>
          }

          @if (!hasTerms() && !effectiveInclusions().length && !effectiveExclusions().length && !hasAnyDaySchedule()) {
          <div class="mt-4">
            <div class="mb-2">Inclusions/Exclusions and Daywise Schedule not provided!</div>
            <button type="button" class="btn btn-primary btn-sm" (click)="editDetail()">
              Edit Inclusions/Exclusions and Itinerary
            </button>
          </div>
          }
        </div>
      </div> <!-- /basic-details-main -->

<!-- ─── Arrival / Departure (Basic Details side column) ─── -->
      <!-- Only shown once a package has been converted; nothing to schedule against beforehand. -->
      @if (isConverted()) {
      <div class="basic-details-side schedule-side-container">
        
        <div class="schedule-info-card">
          <div class="trip-detail-panel-head">
            <span class="panel-title">Arrival Details</span>
            <button type="button" class="edit-btn-circle" (click)="openScheduleModal('Arrival')" title="Edit arrival details">
              <i class="bx bx-pencil"></i>
            </button>
          </div>
          @if (arrivalDetail().length) {
            @for (entry of arrivalDetail(); track $index) {
            <div class="trip-detail-entry">
              <div class="trip-detail-datetime">{{ formatScheduleDateTime(entry.DateTime) }}</div>
              @if (entry.Details) {
              <div class="trip-detail-note">{{ entry.Details }}</div>
              }
            </div>
            }
          } @else {
            <div class="trip-detail-empty">Not Set</div>
          }
        </div>

        <div class="schedule-info-card mt-3">
          <div class="trip-detail-panel-head">
            <span class="panel-title">Departure Details</span>
            <button type="button" class="edit-btn-circle" (click)="openScheduleModal('Departure')" title="Edit departure details">
              <i class="bx bx-pencil"></i>
            </button>
          </div>
          @if (departureDetail().length) {
            @for (entry of departureDetail(); track $index) {
            <div class="trip-detail-entry">
              <div class="trip-detail-datetime">{{ formatScheduleDateTime(entry.DateTime) }}</div>
              @if (entry.Details) {
              <div class="trip-detail-note">{{ entry.Details }}</div>
              }
            </div>
            }
          } @else {
            <div class="trip-detail-empty">Not Set</div>
          }
        </div>
        
      </div> <!-- /basic-details-side -->
      }

    </div> <!-- /basic-details-layout -->
    }

    <!-- ═══════════ ALL QUOTES TAB ═══════════ -->
    @if (activeTab() === 'quotes') {

    <!-- Package Quote Price -->
    <div class="quote-card">
      <div class="section-title">Package Quote Price</div>
      <div class="package-price-grid">
        @for (pkg of packageTypes(); track pkg.QuotePackageTypeId; let idx = $index) {
        <div class="package-price"
          [class.package-price-converted]="isConverted() && pkg.QuotePackageTypeId === convertedPackageTypeId()"
          [class.package-price-onhold]="isOnHold() && pkg.QuotePackageTypeId === convertedPackageTypeId()">
          @if (isOnHold() && pkg.QuotePackageTypeId === convertedPackageTypeId()) {
          <span class="onhold-chip">Used for Holding</span>
          }
          <div class="package-name">Option {{ idx + 1 }}: {{ pkg.PackageTypeName }}</div>
          <div>
            <span class="currency">INR</span>
            <strong>{{ formatCurrency(packageQuotePrice(pkg.QuotePackageTypeId)) }}</strong>
            @if (isGstIncluded(pkg.QuotePackageTypeId)) {
            <span class="small text-muted ms-1">(inc. {{ quoteHeader()?.GstPercent || 0 }}% GST)</span>
            } @else {
            <span class="small text-muted ms-1">(excl. GST)</span>
            }
            @if (isOverallPricing(pkg.QuotePackageTypeId)) {
            <span class="small text-muted ms-1">(Overall Package Price)</span>
            }
          </div>
          <div class="small text-muted">
            INR {{ formatCurrency(packageCostPrice(pkg.QuotePackageTypeId)) }} (cost price)
          </div>
        </div>
        }
      </div>
    </div>

    <!-- ── Hotels + Special Inclusions per package ── -->
    @for (pkg of packageTypes(); track pkg.QuotePackageTypeId; let pkgIdx = $index) {
    <div class="quote-card">
      <span class="option-label">Option {{ pkgIdx + 1 }}: {{ pkg.PackageTypeName }}</span>

      <div class="service-section mb-4">
        <div class="service-icon"><i class="bx bx-bed"></i></div>
        <div class="service-content">
          <h5>Accommodation</h5>
          <div class="quote-table mb-2">
            <div class="quote-row quote-head">
              <div>Night</div>
              <div>Hotel</div>
              <div>Meal</div>
              <div>Rooms</div>
              <div class="text-end">Price</div>
            </div>
            @for (group of hotelGroupsByPackage(pkg.QuotePackageTypeId); track (group.main.QuoteHotelId || $index) + '-'
            + group.nightNumber) {
            <div class="quote-row">
              <div>
                {{ group.nightNumber }}{{ ordinal(group.nightNumber) }}
                <span>{{ group.stayDate | date:'dd MMM' }}</span>
              </div>
              <div class="hotel-cell">
                <div class="hotel-line hotel-line-main">
                  <span class="hotel-name">{{ group.main.HotelName }}</span>
                  @if (group.similar.length) {
                  <i class="bx bx-link-alt text-muted small ms-1" title="Similar hotels available"></i>
                  }
                  <span>{{ group.main.LocationName || '-' }}, {{ group.main.HotelCategoryName || '-' }}</span>
                </div>
                @for (sim of group.similar; track sim.QuoteHotelId) {
                <div class="hotel-line hotel-line-similar">
                  <span class="similar-slash">/</span>
                  <span class="hotel-name">{{ sim.HotelName }}</span>
                  <span>{{ sim.HotelCategoryName || '-' }}</span>
                  <div class="similar-room-detail">
                    {{ sim.NoOfRooms || 1 }} {{ sim.RoomTypeName || 'Room' }}
                    <span>({{ sim.PaxPerRoom || 0 }}A + {{ sim.AWEB || 0 }} AwEB + {{ sim.CNB || 0 }} CNB)</span>
                  </div>
                </div>
                }
              </div>
              <div>{{ group.main.MealPlan || '-' }}</div>
              <div>
                {{ group.main.NoOfRooms || 1 }} {{ group.main.RoomTypeName || 'Room' }}
                <span>{{ group.main.AWEB || 0 }} AwEB + {{ group.main.CNB || 0 }} CNB</span>
              </div>
              <div class="price-cell">
                <i class="bx bx-check-circle text-success"></i>
                <span>INR</span>
                {{ formatCurrency(group.maxPrice) }}
              </div>
            </div>
            } @empty {
            <div class="empty-row">No accommodation added for this package.</div>
            }
          </div>

          <!-- ── QuoteSpecialInclusions table ── -->
          @if (specialInclusionsByPackage(pkg.QuotePackageTypeId).length) {
          <h5 class="mt-3">Hotel Special Inclusions</h5>
          <table class="special-inc-table mb-2">
            <thead>
              <tr>
                <th>Night</th>
                <th>Hotel</th>
                <th>Location / Category</th>
                <th>Special Inclusion</th>
                <th class="text-end">Price (INR)</th>
              </tr>
            </thead>
            <tbody>
              @for (si of specialInclusionsByPackage(pkg.QuotePackageTypeId); track si.QuoteSpecialInclusionId ||
              $index) {
              <tr>
                <td>
                  <strong>{{ si.NightNumber }}{{ ordinal(si.NightNumber) }}</strong>
                  <div class="text-muted text-11">{{ nightDate(si.NightNumber) | date:'dd MMM' }}</div>
                </td>
                <td><strong>{{ si.HotelName }}</strong></td>
                <td class="text-muted">{{ hotelLocationCategory(si.HotelId) }}</td>
                <td>
                  <strong>{{ si.SpecialInclusionName }}</strong>
                  @if (si.Comments) { <span class="d-block text-muted text-11">{{ si.Comments }}</span>
                  }
                </td>
                <td class="text-end"><strong>{{ formatCurrency(si.TotalPrice) }}</strong></td>
              </tr>
              }
            </tbody>
          </table>
          }

          <div class="section-total">
            Total <span>INR</span> {{ formatCurrency(packageHotelTotal(pkg.QuotePackageTypeId) +
            packageSpecialInclusionTotal(pkg.QuotePackageTypeId)) }}
          </div>
        </div>
      </div>
    </div>
    }

    <!-- ── Transportation & Activities (QuoteTransportServices + QuoteActivityEntries) ── -->
    @if (transportServices().length || activityGroups().length) {
    <div class="quote-card">
      <div class="service-section">
        <div class="service-icon"><i class="bx bx-car"></i></div>
        <div class="service-content">
          <h5>Transportation and Activities</h5>

          @for (day of daySlots(); track day.dayNumber) {
          @if (dayHasServices(day.dayNumber)) {
          <div class="day-service">
            <div class="day-box">
              <strong>{{ day.dayNumber }}{{ ordinal(day.dayNumber) }} Day</strong>
              <span>{{ day.date | date:'EEE, dd MMM' }}</span>
            </div>
            <div class="day-lines">

              <!-- ── QuoteTransportServices rows for this day ── -->
              @for (svc of servicesForDay(day.dayNumber); track svc.QuoteServiceId || $index) {
              @if (svc.ServiceType == 1) {
              <div class="day-line">
                <div>
                  <strong>{{ serviceTitle(svc) }}</strong>
                  <span>{{ serviceSubtitle(svc) }}</span>
                </div>
                <div>
                  {{ svc.VehicleTypeName || 'Vehicle' }}
                  <span>{{ serviceBreakdown(svc) }}</span>
                </div>
                <div class="price-cell">
                  <i class="bx bx-check-circle text-success"></i>
                  <span>INR</span>
                  {{ formatCurrency(svc.TotalPrice || svc.SellingPrice || 0) }}
                </div>
              </div>
              }
              }

              <!-- ── QuoteActivityEntries grouped by service ── -->
              @for (group of activityGroupsForDay(day.dayNumber); track group.LocationId + '-' +
              group.ActivityServiceId) {
              <div class="day-line">
                <div>
                  <strong>{{ group.LocationName }}</strong>
                  <span>{{ group.ActivityServiceName }}</span>
                </div>
                <div>
                  @for (entry of group.entries; track entry.QuoteActivityEntryId || $index) {
                  <div>{{ entry.Qty }} {{ entry.PaxTypeLabel || entry.PaxType || 'Adult' }}
                    <span>{{ formatCurrency(entry.Rate) }} × {{ entry.Qty }}</span>
                  </div>
                  }
                </div>
                <div class="price-cell">
                  <i class="bx bx-check-circle text-success"></i>
                  <span>INR</span>
                  {{ formatCurrency(group.total) }}
                </div>
              </div>
              }
            </div>
          </div>
          }
          }

          <div class="section-total">
            Total <span>INR</span> {{ formatCurrency(transportActivityTotal()) }}
            <small>Transports: INR {{ formatCurrency(transportTotal()) }} • Activity/Tickets: INR {{
              formatCurrency(activityTotal()) }}</small>
          </div>
        </div>
      </div>
    </div>
    }

    <!-- ── Detailed QuoteTransportServices table ── -->
    @if (transportServices().length) {
    <div class="quote-card">
      <div class="section-title">Transport Services Detail</div>
      <table class="transport-services-table">
        <thead>
          <tr>
            <th>Day</th>
            <th>Date</th>
            <th>Location / Service</th>
            <th>Vehicle</th>
            <th class="text-end">Qty</th>
            <th class="text-end">Cost (INR)</th>
            <th class="text-end">Selling (INR)</th>
          </tr>
        </thead>
        <tbody>
          @for (svc of transportServices(); track svc.QuoteServiceId || $index) {
          <tr>
            <td><strong>Day {{ svc.DayNumber }}</strong></td>
            <td class="text-muted">{{ svc.ServiceDate | date:'dd MMM' }}</td>
            <td>
              <strong>{{ svc.LocationName || svc.IteneraryServiceName || '-' }}</strong>
              @if (svc.IteneraryServiceName && svc.LocationName) {
              <span class="d-block text-muted text-11">{{ svc.IteneraryServiceName }}</span>
              }
            </td>
            <td>{{ svc.VehicleTypeName || '-' }}</td>
            <td class="text-end">{{ svc.Qty || 1 }}</td>
            <td class="text-end">{{ formatCurrency(svc.CostPrice || 0) }}</td>
            <td class="text-end"><strong>{{ formatCurrency(svc.SellingPrice || svc.TotalPrice || 0) }}</strong></td>
          </tr>
          }
        </tbody>
        <tfoot>
          <tr class="table-row-highlight">
            <td colspan="6" class="fw-bold text-end">Transport Total:</td>
            <td class="text-end fw-bold text-primary">INR {{ formatCurrency(transportTotal()) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    }

    <!-- ── Detailed QuoteActivityEntries table ── -->
    @if (activities().length) {
    <div class="quote-card">
      <div class="section-title">Activity / Ticket Entries Detail</div>
      <table class="activity-entries-table">
        <thead>
          <tr>
            <th>Day</th>
            <th>Date</th>
            <th>Location</th>
            <th>Activity / Ferry</th>
            <th>Pax Type</th>
            <th class="text-end">Qty</th>
            <th class="text-end">Rate (INR)</th>
            <th class="text-end">Selling (INR)</th>
          </tr>
        </thead>
        <tbody>
          @for (entry of activities(); track entry.QuoteActivityEntryId || $index) {
          <tr>
            <td><strong>Day {{ entry.DayNumber }}</strong></td>
            <td class="text-muted">{{ entry.ServiceDate | date:'dd MMM' }}</td>
            <td>{{ entry.LocationName || '-' }}</td>
            <td>
              <strong>{{ entry.ActivityServiceName || '-' }}</strong>
            </td>
            <td>
              <span class="badge bg-light text-dark border text-11">
                {{ entry.PaxTypeLabel || entry.PaxType || 'Adult' }}
              </span>
            </td>
            <td class="text-end">{{ entry.Qty || 1 }}</td>
            <td class="text-end">{{ formatCurrency(entry.Rate || 0) }}</td>
            <td class="text-end"><strong>{{ formatCurrency(entry.SellingPrice || entry.GivenPrice || 0) }}</strong></td>
          </tr>
          }
        </tbody>
        <tfoot>
          <tr class="table-row-highlight">
            <td colspan="7" class="fw-bold text-end">Activity Total:</td>
            <td class="text-end fw-bold text-primary">INR {{ formatCurrency(activityTotal()) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    }

    <!-- Day-wise Schedule -->
    <div class="quote-card">
      <div class="collapsible-title"><i class="bx bx-chevron-down"></i> Day-wise Schedule</div>
      @for (day of daySlots(); track day.dayNumber) {
      @if (daySchedule(day.dayNumber); as sched) {
      <div class="schedule-item">
        <div class="schedule-date">
          <strong>{{ day.dayNumber }}{{ ordinal(day.dayNumber) }} Day</strong>
          <span>{{ day.dayLabel }}</span>
          <b>{{ day.date | date:'dd MMM' }}</b>
          <span>{{ day.date | date:'yyyy' }}</span>
        </div>
        <div class="schedule-copy">
          <h5>{{ sched.title }}</h5>
          @if (rawDaySchedule(day.dayNumber); as rawHtml) {
          <div [innerHTML]="rawHtml"></div>
          }
          @for (group of activityGroupsForDay(day.dayNumber); track group.LocationId + '-' + group.ActivityServiceId) {
          <h6>{{ activityGroupTitle(group) }}</h6>
          }
        </div>
      </div>
      }
      }
    </div>

    <!-- Inclusions / Exclusions -->
    <div class="quote-card">
      <div>
        <div class="d-flex align-items-center justify-content-between">
          <div class="collapsible-title"><i class="bx bx-chevron-down"></i> Inclusions/Exclusions</div>
          <button type="button" class="btn btn-outline-primary btn-sm" (click)="openIncExcModal()">
            Add/Edit Inc-Exclusion
          </button>
        </div>
        <div class="inc-exc-grid">
          <div>
            <span class="green-underline">Inclusions</span>
            <ul class="tick-list">
              @for (item of effectiveInclusions(); track item.QuoteInclusionId || item.InclusionId || $index) {
              <li>{{ inclusionText(item) }}</li>
              } @empty {
              <li class="text-muted">No inclusions added for this quotation.</li>
              }
            </ul>
          </div>
          <div>
            <span class="red-underline">Exclusions</span>
            <ul class="cross-list">
              @for (item of effectiveExclusions(); track item.QuoteExclusionId || item.ExclusionId || $index) {
              <li>{{ exclusionText(item) }}</li>
              } @empty {
              <li class="text-muted">No exclusions added for this quotation.</li>
              }
            </ul>
            <div class="exclusion-note">Anything not in inclusions is Excluded</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Terms -->
    <div class="quote-card">
      <div class="terms-box">
        <div class="terms-head"><i class="bx bx-chevron-down"></i> Terms and Conditions</div>
        <div class="terms-content">
          <h5>{{ tripInfo()?.DestinationName || 'Terms' }}</h5>
          @if (hasTerms()) {
          @for (term of terms(); track term.TermAndConditionId || $index) {
          <div class="mb-2" [innerHTML]="termHtml(term)"></div>
          }
          } @else {
          <div class="text-muted">No terms and conditions added for this destination.</div>
          }
        </div>
      </div>
    </div>

    } <!-- /activeTab() === 'quotes' -->

<!-- ═══════════════════════════════════════════════════
     DOCS TAB (converted trips only) — Redesigned
     ═══════════════════════════════════════════════════ -->
@if (activeTab() === 'docs' && isConverted()) {
  <div class="docs-workspace">

    <!-- ── Toolbar Card ── -->
    <div class="docs-toolbar">

      <!-- Header -->
      <div class="docs-toolbar__header">
        <div class="docs-toolbar__title">
          <span class="docs-toolbar__icon"><i class="bx bx-slider-alt"></i></span>
          <div>
            <div class="docs-toolbar__heading">Voucher Builder</div>
            <div class="docs-toolbar__subheading">Choose which sections appear on the voucher</div>
          </div>
        </div>
        <span class="docs-toolbar__badge">Live Preview</span>
      </div>

      <!-- Toggles + Actions -->
      <div class="docs-toolbar__body">

        <!-- Toggle groups -->
        <div class="docs-toggles">

          <!-- Show -->
          <div class="docs-toggle-group">
            <span class="docs-toggle-group__label">Show</span>
            <div class="docs-toggle-group__items">

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherPrices"
                  [checked]="voucherShowPrices()" (change)="toggleVoucherShowPrices()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">Prices</span>
              </label>

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherSupplierContact"
                  [checked]="voucherShowSupplierContact()" (change)="toggleVoucherShowSupplierContact()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">Supplier Contact</span>
              </label>

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherBankAccount"
                  [checked]="voucherShowBankAccount()" (change)="toggleVoucherShowBankAccount()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">Bank Account</span>
              </label>

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherGuestList"
                  [checked]="voucherShowGuestList()" (change)="toggleVoucherShowGuestList()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">Guest List</span>
              </label>

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherTnC"
                  [checked]="voucherShowTnC()" (change)="toggleVoucherShowTnC()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">T&amp;C</span>
              </label>

            </div>
          </div>

          <!-- Hide -->
          <div class="docs-toggle-group">
            <span class="docs-toggle-group__label">Hide</span>
            <div class="docs-toggle-group__items">

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherRemoveBranding"
                  [checked]="voucherRemoveBranding()" (change)="toggleVoucherRemoveBranding()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">Remove Branding</span>
              </label>

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherRemoveHotels"
                  [checked]="voucherRemoveHotels()" (change)="toggleVoucherRemoveHotels()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">Remove Hotels</span>
              </label>

              <label class="docs-switch">
                <input class="docs-switch__input" type="checkbox" id="voucherRemoveItinerary"
                  [checked]="voucherRemoveFullItinerary()" (change)="toggleVoucherRemoveFullItinerary()">
                <span class="docs-switch__track"><span class="docs-switch__thumb"></span></span>
                <span class="docs-switch__label">No Itinerary</span>
              </label>

            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="docs-actions">
          <button type="button" class="docs-icon-btn" (click)="loadVoucherGuestsIfNeeded()" title="Refresh guest list">
            <i class="bx bx-refresh"></i>
          </button>

          <button type="button" class="docs-btn docs-btn--copy" (click)="copyVoucherHtml()">
            <i class="bx bx-copy"></i> Copy
          </button>

          <div class="docs-btn-group">
            <button type="button" class="docs-btn" (click)="downloadVoucherPdf()">
              <i class="bx bxs-file-pdf text-danger"></i> PDF
            </button>
            <button type="button" class="docs-btn" (click)="downloadVoucherWordDoc()">
              <i class="bx bxs-file-doc text-primary"></i> Word
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Preview Canvas ── -->
    <div class="docs-preview">
      <div class="docs-preview__paper voucher-preview-container">
        <div class="constrained-voucher-content" [innerHTML]="voucherPreviewHtml()"></div>
      </div>
    </div>

  </div>
}

  </main>
</div>
}

<!-- ══════════════════════════════════════════════════════════════
     SHARE MODAL
     ══════════════════════════════════════════════════════════════ -->
@if (shareOpen()) {
<div class="modal d-block modal-backdrop-dim" tabindex="-1">
  <div class="modal-dialog modal-lg modal-dialog-scrollable">
    <div class="modal-content">
      <div class="modal-header">
        <ul class="nav nav-tabs card-header-tabs border-0">
          <li class="nav-item">
            <a class="nav-link" [class.active]="shareChannel() === 'whatsapp'" href="javascript:void(0)"
              (click)="setShareChannel('whatsapp')">WhatsApp</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" [class.active]="shareChannel() === 'email'" href="javascript:void(0)"
              (click)="setShareChannel('email')">Email</a>
          </li>
        </ul>
        <button type="button" class="btn-close" (click)="closeShare()"></button>
      </div>

      <div class="modal-body">
        <div class="d-flex flex-wrap gap-3 mb-3 small">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" [checked]="hideTotalPrice()"
              (change)="toggleHideTotalPrice()">
            <label class="form-check-label">Hide Total Price</label>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" [checked]="removeItinerary()"
              (change)="toggleRemoveItinerary()">
            <label class="form-check-label">Remove Itinerary</label>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" [checked]="removeTerms()" (change)="toggleRemoveTerms()">
            <label class="form-check-label">Remove Terms</label>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" [checked]="removeTransportActivities()"
              (change)="toggleRemoveTransportActivities()">
            <label class="form-check-label">Remove Transports &amp; Activities</label>
          </div>
        </div>

        @if (shareChannel() === 'whatsapp') {
        <pre class="border rounded p-3 bg-light small whatsapp-preview-text"
         >{{ buildWhatsAppText() }}</pre>
        } @else {
        <div class="border rounded p-3" [innerHTML]="buildEmailHtml()"></div>
        }
      </div>

      <div class="modal-footer">
        @if (shareChannel() === 'whatsapp') {
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="copyWhatsAppText()">
          <i class="bx bx-copy me-1"></i>Copy
        </button>
        <button type="button" class="btn btn-success btn-sm" (click)="sendWhatsApp()">
          <i class="bx bxl-whatsapp me-1"></i>Send via WhatsApp
        </button>
        } @else {
        <button type="button" class="btn btn-outline-secondary btn-sm" (click)="copyEmailHtml()">
          <i class="bx bx-copy me-1"></i>Copy
        </button>
        }
        <!-- <button type="button" class="btn btn-warning btn-sm text-dark" (click)="downloadWordDoc()" [disabled]="pdfLoading()">
          <i class="bx bx-file-pdf me-1"></i>Download WORD
        </button> -->
        <button type="button" class="btn btn-danger btn-sm" (click)="downloadPdf()" [disabled]="pdfLoading()">
          <i class="bx bx-file-pdf me-1"></i>Download PDF
        </button>
      </div>
    </div>
  </div>
</div>
}

<!-- ══════════════════════════════════════════════════════════════
     ARRIVAL / DEPARTURE DETAILS MODAL
     ══════════════════════════════════════════════════════════════ -->
@if (scheduleModalOpen()) {
<div class="modal d-block modal-backdrop-dim" tabindex="-1">
  <div class="modal-dialog modal-dialog-scrollable">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Update {{ scheduleModalType() }} Details</h5>
        <button type="button" class="btn-close" (click)="closeScheduleModal()"></button>
      </div>

      <div class="modal-body">
        @for (entry of scheduleEntries(); track $index; let idx = $index) {
        <div class="trip-detail-modal-entry">
          @if (scheduleEntries().length > 1) {
          <button type="button" class="btn btn-sm btn-outline-danger remove-entry-btn"
            (click)="removeScheduleEntry(idx)">
            <i class="bx bx-x"></i>
          </button>
          }

          <label class="form-label small fw-semibold">Date and Time</label>
          <input type="datetime-local" class="form-control mb-3" [value]="entry.DateTime"
            (change)="onScheduleDateTimeChange(idx, $event)" />

          <label class="form-label small fw-semibold">Details</label>
          <textarea class="form-control" rows="3"
            placeholder="Add details here.&#10;Use new lines to enter multiple details." [value]="entry.Details"
            (change)="onScheduleDetailsChange(idx, $event)"></textarea>
        </div>
        }

        <button type="button" class="btn btn-outline-primary btn-sm mt-3" (click)="addScheduleEntry()">
          Add More
        </button>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-primary btn-sm" (click)="saveScheduleDetail()"
          [disabled]="savingSchedule()">
          @if (savingSchedule()) {
          <span class="spinner-border spinner-border-sm me-1"></span>Saving…
          } @else {
          Save Details
          }
        </button>
        <button type="button" class="btn btn-light btn-sm border" (click)="closeScheduleModal()">Cancel</button>
      </div>
    </div>
  </div>
</div>
}

@if (incExcModalOpen()) {
<div class="modal d-block modal-backdrop-dim" tabindex="-1">
  <div class="modal-dialog modal-dialog-scrollable modal-dialog-wide">

    <div class="modal-content inc-exc-modal">

      <div class="modal-header modal-header-teal">
        <h5 class="modal-title">Add/Edit Inclusions &amp; Exclusions</h5>

        <button type="button" class="btn-close" (click)="closeIncExcModal()">
        </button>
      </div>

      <div class="modal-body">

        <div class="text-muted mb-3">
          These apply to this quotation only and appear in WhatsApp, email, and PDF exports for it.
        </div>

        <div class="row">

          <div class="col-md-6">

            <div class="green-underline mb-2 fs-3">
              Inclusions
            </div>

            @for (line of incExcInclusionLines(); track $index; let idx = $index) {
            <div class="trip-detail-modal-entry">

              @if (incExcInclusionLines().length > 1) {
              <button type="button" class="btn btn-sm btn-outline-danger remove-entry-btn" (click)="removeIncLine(idx)">
                <i class="bx bx-x"></i>
              </button>
              }

              <textarea class="form-control" rows="2" placeholder="e.g. Accommodation on double sharing basis"
                [value]="line.InclusionText" (change)="onIncLineChange(idx, $event)">
              </textarea>

            </div>
            }

            <button type="button" class="btn btn-outline-primary mt-2" (click)="addIncLine()">
              Add Inclusion
            </button>

          </div>

          <div class="col-md-6">

            <div class="red-underline mb-2 fs-3">
              Exclusions
            </div>

            @for (line of incExcExclusionLines(); track $index; let idx = $index) {
            <div class="trip-detail-modal-entry">

              @if (incExcExclusionLines().length > 1) {
              <button type="button" class="btn btn-sm btn-outline-danger remove-entry-btn" (click)="removeExcLine(idx)">
                <i class="bx bx-x"></i>
              </button>
              }

              <textarea class="form-control" rows="2" placeholder="e.g. Airfare / Ship's fare"
                [value]="line.ExclusionText" (change)="onExcLineChange(idx, $event)">
              </textarea>

            </div>
            }

            <button type="button" class="btn btn-outline-primary mt-2" (click)="addExcLine()">
              Add Exclusion
            </button>

          </div>

        </div>
      </div>

      <div class="modal-footer">

        <button type="button" class="btn btn-primary w-18" (click)="saveIncExc()" [disabled]="savingIncExc()">

          @if (savingIncExc()) {
          <span class="spinner-border spinner-border-sm me-1"></span>
          Saving…
          } @else {
          Save
          }

        </button>

        <button type="button" class="btn btn-light border w-18" (click)="closeIncExcModal()">
          Cancel
        </button>

      </div>

    </div>
  </div>
</div>
}

<!-- ══════════════════════════════════════════════════════════════ -->
<!-- EDIT GUEST (TOURIST MANAGEMENT) MODAL                          -->
<!-- Adapted from query-steptwo.html's Trip Tourists Management     -->
<!-- modal — same GetGuestByTrip/SaveGuestList flow, same row shape -->
<!-- ══════════════════════════════════════════════════════════════ -->
@if (showTouristModal()) {

  <!-- Backdrop -->
  <div class="modal-backdrop fade show modal-backdrop-blur"
       (click)="closeTouristModal()"></div>

  <!-- Dialog -->
  <div class="modal fade show d-block modal-z-1050" tabindex="-1">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg tourist-modal">

        <!-- Header -->
        <div class="modal-header border-bottom px-4 py-3">
          <h5 class="modal-title fw-bold">Trip Tourists Management</h5>
          <button type="button" class="btn-close" (click)="closeTouristModal()"></button>
        </div>

        <!-- Body -->
        <div class="modal-body px-4 py-3">

          <!-- Tourist count -->
          <p class="text-muted mb-3 text-14">
            Showing {{ touristCount }} tourist{{ touristCount !== 1 ? 's' : '' }}
            <i class="bx bx-refresh ms-1 cursor-pointer" (click)="loadTouristsForTrip()"></i>
          </p>

          <!-- Tourist rows -->
          @for (row of touristRows(); track $index; let i = $index) {
          <div class="border rounded mb-3 overflow-hidden">

            <!-- Row header (collapsed view) -->
            <div class="d-flex align-items-center px-3 py-2 bg-light">

              <span class="text-muted me-2 tourist-row-index">{{ i + 1 }}.</span>
              <i class="bx bx-user-circle text-muted me-2 icon-user-circle"></i>

              <div class="flex-grow-1 d-flex align-items-center gap-2">
                @if (row.ContactName) {
                <span class="fw-semibold text-15">{{ row.Salutation }} {{ row.ContactName }}</span>
                } @else {
                <span class="text-muted fst-italic text-14">New Tourist</span>
                }
                @if (row.Phone) {
                <span class="text-muted text-14">
                  <i class="bx bx-phone me-1"></i>{{ row.CountryCode }} {{ row.Phone }}
                </span>
                }
              </div>

              <div class="d-flex align-items-center gap-1">
                <button class="icon-btn-sm" mat-icon-button matTooltip="Remove tourist"
                  (click)="removeTouristRow(i)">
                  <mat-icon class="icon-sm-18" color="warn">delete</mat-icon>
                </button>
                <button class="icon-btn-sm" mat-icon-button (click)="toggleExpand(i)">
                  <mat-icon class="icon-sm-18">{{ row.IsExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
              </div>

            </div>

            <!-- Expanded form -->
            @if (row.IsExpanded) {
            <div class="p-3 border-top bg-white">
              <div class="row g-3">

                
                <!-- Guest Type -->
                <div class="col-sm-2">
                  <label class="form-label mb-1">Type</label>
                  <div class="d-flex gap-2">
                    <select class="form-select flex-shrink-0 select-full-width"
                      [(ngModel)]="row.GuestType">
                      @for (gt of GuestTypes; track gt.Key) {
                      <option [ngValue]="gt.Key">{{ gt.Value }}</option>
                      }
                    </select>
                   </div>
                </div>
                

                <!-- Salutation -->
                <div class="col-sm-1">
                  <label class="form-label mb-1">Salutation</label>
                  <input type="text" class="form-control" [(ngModel)]="row.Salutation"
                    placeholder="e.g. Mr." maxlength="10">
                </div>

                <!-- Name -->
                <div class="col-sm-4">
                  <label class="form-label mb-1">Name *</label>
                  <input type="text" class="form-control" [(ngModel)]="row.ContactName"
                    placeholder="e.g. Guest Name">
                </div>

                <!-- Date of Birth -->
                <div class="col-sm-3">
                  <label class="form-label mb-1">Date of Birth</label>
                  <input type="date" class="form-control" [(ngModel)]="row.DateOfBirth">
                </div>
                
                <!-- Primary -->
                <div class="col-sm-12 d-flex align-items-center">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" [(ngModel)]="row.IsPrimary" id="primary-{{i}}">
                    <label class="form-check-label" for="primary-{{i}}">Primary contact</label>
                  </div>
                </div>

                <!-- Phone -->
                <div class="col-sm-4">
                  <label class="form-label mb-1">Phone Number</label>
                  <div class="d-flex gap-2">
                    <select class="form-select flex-shrink-0 select-fixed-110"
                      [(ngModel)]="row.CountryCode">
                      @for (cc of countryCodes; track cc.code) {
                      <option [value]="cc.code">{{ cc.label }}</option>
                      }
                    </select>
                    <input type="text" class="form-control" [(ngModel)]="row.Phone"
                      placeholder="e.g. 9779212232">
                  </div>
                </div>

                <!-- Email -->
                <div class="col-sm-5">
                  <label class="form-label mb-1">Email</label>
                  <input type="email" class="form-control" [(ngModel)]="row.Email"
                    placeholder="e.g. user@domain.com">
                </div>
                
                                <!-- Nationality -->
                                <div class="col-sm-3">
                                  <label class="form-label mb-1">Nationality</label>
                                  <input type="text" class="form-control" [(ngModel)]="row.Nationality"
                                    placeholder="e.g. India">
                                </div>

              </div>
            </div>
            }

          </div>
          }

          <!-- Add Tourist button -->
          <button class="btn btn-outline-secondary btn-sm mt-1 text-13" (click)="addTouristRow()">
            <i class="bx bx-plus me-1"></i> Add Tourist
          </button>

        </div>

        <!-- Footer -->
        <div class="modal-footer border-top px-4 py-3">
          <button type="button" class="btn btn-outline-secondary px-4" (click)="closeTouristModal()">
            Cancel
          </button>
          <button type="button" class="btn btn-primary px-4 fw-medium" [disabled]="savingTourists()"
            (click)="saveTourists()">
            <i class="bx bx-save me-1"></i>
            {{ savingTourists() ? 'Saving…' : 'Save Tourists' }}
          </button>
        </div>

      </div>
    </div>
  </div>

}